import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeService } from '@shared/services/mesuser.service';
import { IScannerData, ScannerListenerService } from '@shared/services/scanner-listener.service';
import { ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { finalize, Subject, takeUntil } from 'rxjs';
import { MesProductionTerminalComponent } from '../../mes-production-terminal/mes-production-terminal.component';

interface IWorktaskViewModel {
	id: string;
	alias: string;
	name: string;
	categoryName: string;
}

@Component({
	selector: 'app-task-select',
	templateUrl: './task-select.component.html',
	styleUrls: ['./task-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TaskSelectComponent implements OnInit, OnDestroy {
	public workTasks: IWorktaskViewModel[];
	public currentWorkTasks: IWorktaskViewModel[];

	public lastUsedWorktasks: IWorktask[] = [];
	public offTask: IWorktask;
	private offTaskId = '00000000-0000-0000-0000-000000000000';
	private readonly destroy$ = new Subject<void>();
	public categoryGroupNames: string[] = [];
	public categoryGroups: { [key: string]: IWorktaskViewModel[] } = {};
	terminal: ITerminal;

	private _currentCategoryGroupName: string;

	public get currentCategoryGroupName() {
		return this._currentCategoryGroupName;
	}

	public set currentCategoryGroupName(value: string) {
		this.currentWorkTasks = value === this.allCategoriesLabel ? this.workTasks : this.categoryGroups[value];
		this._currentCategoryGroupName = value;
	}

	get filteredFavouriteWorkTasks(): IWorktask[] {
		const favs = this.employeeService.favouriteWorkTasks;
		if (!favs || !this.terminal || !this.terminal.workTaskCategories || this.terminal.workTaskCategories.length === 0) {
			return favs;
		}
		const allowed = new Set(this.terminal.workTaskCategories);
		return favs.filter((t) => allowed.has(t.tags?.['categoryName'] ?? ''));
	}

	private uncategorizedLabel: string;
	private allCategoriesLabel: string;

	constructor(
		public employeeService: EmployeeService,
		public productionTerminal: MesProductionTerminalComponent,
		private workTaskService: WorktaskService,
		private translateService: TranslateService,
		private terminalService: TerminalAdministrationService,
		private router: Router,
		scannerListenerService: ScannerListenerService,
	) {
		// The listener is a root service, so without this a terminal left and reopened keeps the old
		// instance listening, and a task scan checks the employee in once per instance.
		scannerListenerService
			.ScannerDataChanged()
			.pipe(takeUntil(this.destroy$))
			.subscribe((c) => this.onScan(c));
	}

	@Input()
	public employeeId: string;

	ngOnInit(): void {
		this.translateService.get('mes.mes-production-terminal.uncategorized-worktask-label').subscribe((label: string) => {
			this.uncategorizedLabel = label;
		});
		this.translateService.get('mes.mes-production-terminal.all-categories-label').subscribe((label: string) => {
			this.allCategoriesLabel = label;
		});
		const pos = this.router.url.lastIndexOf('/') + 1;
		if (pos < 10) {
			this.getWorkTasks();
		} else {
			const terminalId = this.router.url.substring(pos, pos + 36);
			this.terminalService.getTerminalById(terminalId).subscribe((c) => {
				this.terminal = c;
				this.getWorkTasks();
			});
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onScan(scannerData: IScannerData) {
		if (scannerData.prefix === ScannerListenerService.scannerPrefixes.worktaskPrefix && this.employeeService.currentEmployee) {
			const task = this.workTasks.find((c) => c.alias === scannerData.token);
			if (task) {
				this.checkInEmployeeOnWorktask(task);
			} else {
				this.showTemporaryDialog(this.translateMessage(`mes.mes-production-terminal.choose-worktask.unknown-worktask`, { worktask: scannerData.token }), false);
			}
		}
	}

	onWorkTaskClicked(task: IWorktask | IWorktaskViewModel) {
		this.checkInEmployeeOnWorktask({ id: task.id, name: task.name });
	}

	private checkInEmployeeOnWorktask(task: { id: string; name: string }) {
		let success: boolean = true;
		let apiErrorMessage = '';
		this.employeeService
			.checkIn(this.employeeService.currentEmployee.id, task.id)
			.pipe(
				finalize(() => {
					const params = {
						alias: this.employeeService.currentEmployee.alias,
						firstName: this.employeeService.currentEmployee.firstName,
						lastName: this.employeeService.currentEmployee.lastName,
						workTask: task.name,
					};

					let message = task.id === this.offTaskId ? this.translateMessage(`mes.mes-production-terminal.employeekey-enter.${success ? 'check-out' : 'check-out-failed'}`, params) : this.translateMessage(`mes.mes-production-terminal.employeekey-enter.${success ? 'check-in' : 'check-in-failed'}`, params);

					if (!success) {
						const translatedError = this.translateApiError(apiErrorMessage);
						if (translatedError) {
							message += `\n${translatedError}`;
						}
						message += `\n${this.translateMessage('mes.mes-production-terminal.employeekey-enter.contact-supervisor', {})}`;
					}

					this.showTemporaryDialog(message, success);
					this.employeeService.currentEmployee = null;
				}),
			)
			.subscribe({
				next: () => {
					success = true;
				},
				error: (err) => {
					success = false;
					apiErrorMessage = err.error?.error.message || '';
				},
			});
	}

	private readonly apiErrorPatterns: { pattern: RegExp; key: string }[] = [
		{
			pattern: /Work task with id .* does not exist/i,
			key: 'work-task-not-found',
		},
		{
			pattern: /Work task with id .* is not active/i,
			key: 'work-task-not-active',
		},
		{
			pattern: /Department with id .* does not exist/i,
			key: 'department-not-found',
		},
		{
			pattern: /Department with id .* is not active/i,
			key: 'department-not-active',
		},
		{
			pattern: /Employee with id .* does not exist/i,
			key: 'employee-not-found',
		},
		{
			pattern: /Employee with alias .* is not active/i,
			key: 'employee-not-active',
		},
		{ pattern: /Shift with id .* does not exist/i, key: 'shift-not-found' },
		{
			pattern: /Employee with id .* has no ongoing shifts/i,
			key: 'no-ongoing-shift',
		},
		{
			pattern: /Shift with id .* is already closed/i,
			key: 'shift-already-closed',
		},
		{
			pattern: /duration of a shift cannot exceed (?<hours>[\d.,]+) hours/i,
			key: 'shift-exceeds-max-duration',
		},
		{
			pattern: /Shift creation was called, but no shift was found/i,
			key: 'shift-creation-failed',
		},
		{
			pattern: /Shift cannot overlap existing shift/i,
			key: 'shift-overlap',
		},
		{
			pattern: /Employee with id .* is not assigned a department/i,
			key: 'employee-no-department',
		},
	];

	private translateApiError(apiMessage: string): string {
		if (!apiMessage) return '';
		const match = this.apiErrorPatterns.find((p) => p.pattern.test(apiMessage));
		if (match) {
			return this.translateMessage(`mes.mes-production-terminal.employeekey-enter.api-errors.${match.key}`, match.pattern.exec(apiMessage)?.groups ?? {});
		}
		return apiMessage;
	}

	private getWorkTasks() {
		this.workTaskService.getWorktasks().subscribe((c) => {
			this.workTasks = c.result
				.filter((c) => c.isActive)
				.sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()))
				.map(
					(c) =>
						<IWorktaskViewModel>{
							id: c.id,
							alias: c.alias,
							name: c.name,
							categoryName: c.tags['categoryName'] ?? this.uncategorizedLabel,
						},
				);
			this.offTask = c.result.find((c) => c.id === this.offTaskId);
			this.groupWorkTasksByCategory();
		});
	}

	private groupWorkTasksByCategory() {
		if (!this.terminal || this.terminal.workTaskCategories.length === 0) {
			this.categoryGroupNames = [];
			this.workTasks.forEach((c) => {
				if (this.categoryGroups[c.categoryName]) {
					this.categoryGroups[c.categoryName].push(c);
				} else {
					this.categoryGroups[c.categoryName] = [c];
					if (c.categoryName === this.uncategorizedLabel) {
						this.categoryGroupNames.unshift(c.categoryName);
					} else {
						this.categoryGroupNames.push(c.categoryName);
					}
				}
			});
			this.categoryGroupNames.unshift(this.allCategoriesLabel);
			this.categoryGroupNames = this.categoryGroupNames.sort();
			this.currentCategoryGroupName = this.allCategoriesLabel;
		} else {
			this.terminal.workTaskCategories.forEach((c) => {
				if (c === '') c = this.uncategorizedLabel;
				this.categoryGroups[c] = [];
				this.categoryGroupNames.push(c);
			});
			this.workTasks.forEach((c) => {
				if (this.categoryGroups[c.categoryName]) {
					this.categoryGroups[c.categoryName].push(c);
				}
			});
			this.currentCategoryGroupName = this.categoryGroupNames[0];
		}
	}

	private translateMessage(key: string, params: any): string {
		return this.translateService.instant(key, params);
	}

	private showTemporaryDialog(message: string, success: boolean) {
		this.productionTerminal.dialogMessage = message;
		this.productionTerminal.dialogIsError = !success;
		setTimeout(
			() => {
				this.productionTerminal.dialogMessage = null;
				this.productionTerminal.dialogIsError = false;
			},
			success ? 2500 : 6000,
		);
	}
}
